import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  Language as WebsiteIcon,
  Instagram as InstagramIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@material-ui/icons'
import {
  useGetNonprofitOrgProfileQuery,
  useRetriggerProfilerMutation,
} from '../../../../Generated/graphql-types'
import { colors } from '../../../../Themes/Colors'
import {
  PageContainer,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  InfoCard,
  SectionTitle,
  ProfileSection,
  ProfileGrid,
  ProfileColumn,
  ProfileLabel,
  ProfileContent,
  TagContainer,
  DataSection,
  DataSectionHeader,
  DataContent,
  JSONBlock,
  LoadingContainer,
  ErrorContainer,
  EmptyState,
  RefreshButton,
} from './NonprofitProfile.styled'

interface RouteParams {
  id: string
}

export const NonprofitProfile: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const nonprofitId = parseInt(id, 10)

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    candid: false,
    website: false,
    instagram: false,
  })

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  )

  const { data, loading, error, refetch } = useGetNonprofitOrgProfileQuery({
    variables: { nonprofitOrgId: nonprofitId },
    skip: !nonprofitId,
  })

  const [retriggerProfiler, { loading: retriggerLoading }] =
    useRetriggerProfilerMutation({
      onCompleted: () => {
        // Refetch the profile data after retriggering
        refetch()
        setSnackbarMessage('Profile refresh initiated successfully')
        setSnackbarSeverity('success')
        setSnackbarOpen(true)
      },
      onError: (retriggerError) => {
        const message = retriggerError.message.includes(
          'There was an error with your request'
        )
          ? 'You do not have permission to refresh this profile. Please contact an administrator.'
          : `Failed to refresh profile: ${retriggerError.message}`
        setSnackbarMessage(message)
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      },
    })

  const handleBack = () => {
    history.push('/internal/self-serve/nonprofit-admin')
  }

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleRetrigger = async () => {
    try {
      await retriggerProfiler({ variables: { nonprofitOrgId: nonprofitId } })
    } catch (err) {
      // Error handling is done in the onError callback
    }
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  if (error) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Nonprofit Admin
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Error Loading Profile
          </Typography>
          <Typography>{error.message}</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (loading) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Nonprofit Admin
        </BackButton>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    )
  }

  const profile = data?.nonprofitOrgProfile

  if (!profile) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Nonprofit Admin
        </BackButton>
        <EmptyState>
          <BusinessIcon
            style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }}
          />
          <Typography variant='h5' gutterBottom>
            No Profile Found
          </Typography>
          <Typography color='textSecondary' paragraph>
            This nonprofit doesn&apos;t have a profile yet.
          </Typography>
          <Button
            variant='contained'
            startIcon={<RefreshIcon />}
            onClick={handleRetrigger}
            disabled={retriggerLoading}
            style={{
              backgroundColor: colors.resiliaBlue,
              color: 'white',
            }}
          >
            Generate Profile
          </Button>
        </EmptyState>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth='lg'>
      <HeaderSection>
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant='text'
        >
          Back to Nonprofit Admin
        </BackButton>

        <TitleSection>
          <Box>
            <Title>Nonprofit Profile</Title>
            <Subtitle>
              Organization ID: {nonprofitId}
              {profile.nonprofitOrgId && ` • Profile ID: ${profile.id}`}
            </Subtitle>
          </Box>
          <RefreshButton
            variant='contained'
            startIcon={<RefreshIcon />}
            onClick={handleRetrigger}
            disabled={retriggerLoading}
            size='small'
          >
            {retriggerLoading ? 'Regenerating...' : 'Regenerate Profile'}
          </RefreshButton>
        </TitleSection>
      </HeaderSection>

      {/* Profile Summary Section */}
      <InfoCard>
        <CardContent>
          <SectionTitle>
            <InfoIcon />
            Profile Summary
          </SectionTitle>

          <ProfileGrid container spacing={4}>
            <Grid item xs={12} md={6}>
              <ProfileSection>
                <ProfileColumn>
                  <ProfileLabel>AI Generated Summary</ProfileLabel>
                  <ProfileContent>
                    {profile.suggestedOrgSummary || (
                      <Typography color='textSecondary' variant='body2'>
                        No generated summary available
                      </Typography>
                    )}
                  </ProfileContent>

                  <ProfileLabel>AI Generated Areas of Impact</ProfileLabel>
                  <TagContainer>
                    {profile.suggestedAreasOfImpact?.length > 0 ? (
                      profile.suggestedAreasOfImpact.map((area) => (
                        <Chip
                          key={area}
                          label={area}
                          size='small'
                          style={{
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                          }}
                        />
                      ))
                    ) : (
                      <Typography color='textSecondary' variant='body2'>
                        No areas of impact generated
                      </Typography>
                    )}
                  </TagContainer>
                </ProfileColumn>
              </ProfileSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <ProfileSection confirmed>
                <ProfileColumn>
                  <ProfileLabel>Confirmed Summary</ProfileLabel>
                  <ProfileContent>
                    {profile.confirmedOrgSummary || (
                      <Typography color='textSecondary' variant='body2'>
                        No confirmed summary available
                      </Typography>
                    )}
                  </ProfileContent>

                  <ProfileLabel>Confirmed Areas of Impact</ProfileLabel>
                  <TagContainer>
                    {profile.confirmedAreasOfImpact?.length > 0 ? (
                      profile.confirmedAreasOfImpact.map((area) => (
                        <Chip
                          key={area}
                          label={area}
                          size='small'
                          style={{
                            backgroundColor: '#e8f5e9',
                            color: colors.darkGreen,
                          }}
                        />
                      ))
                    ) : (
                      <Typography color='textSecondary' variant='body2'>
                        No confirmed areas of impact
                      </Typography>
                    )}
                  </TagContainer>
                </ProfileColumn>
              </ProfileSection>
            </Grid>
          </ProfileGrid>
        </CardContent>
      </InfoCard>

      {/* Data Sources Section */}
      <Typography
        variant='h5'
        style={{ margin: '32px 0 16px 0', color: colors.greyBlue }}
      >
        Data Sources
      </Typography>

      {/* Candid Data */}
      <DataSection>
        <DataSectionHeader onClick={() => toggleSection('candid')}>
          <Box display='flex' alignItems='center' gap={1}>
            <CodeIcon />
            <Typography variant='h6'>Candid Data</Typography>
            {profile.candidData && (
              <Chip label='Available' size='small' color='success' />
            )}
          </Box>
          <IconButton size='small'>
            {expandedSections.candid ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </DataSectionHeader>

        <Collapse in={expandedSections.candid}>
          <DataContent>
            {profile.candidData ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' gutterBottom>
                    Summary
                  </Typography>
                  <JSONBlock>
                    {JSON.stringify(profile.candidData?.data?.summary, null, 2)}
                  </JSONBlock>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' gutterBottom>
                    Programs
                  </Typography>
                  <JSONBlock>
                    {JSON.stringify(
                      profile.candidData?.data?.programs,
                      null,
                      2
                    )}
                  </JSONBlock>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' gutterBottom>
                    Financials
                  </Typography>
                  <JSONBlock>
                    {JSON.stringify(
                      profile.candidData?.data?.financials,
                      null,
                      2
                    )}
                  </JSONBlock>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant='subtitle2' gutterBottom>
                    Operations
                  </Typography>
                  <JSONBlock>
                    {JSON.stringify(
                      profile.candidData?.data?.operations,
                      null,
                      2
                    )}
                  </JSONBlock>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='subtitle2' gutterBottom>
                    Charity Check
                  </Typography>
                  <JSONBlock>
                    {JSON.stringify(
                      profile.candidData?.data?.charitycheck,
                      null,
                      2
                    )}
                  </JSONBlock>
                </Grid>
              </Grid>
            ) : (
              <EmptyState small>
                <Typography color='textSecondary'>
                  No Candid data available
                </Typography>
              </EmptyState>
            )}
          </DataContent>
        </Collapse>
      </DataSection>

      {/* Website Data */}
      <DataSection>
        <DataSectionHeader onClick={() => toggleSection('website')}>
          <Box display='flex' alignItems='center' gap={1}>
            <WebsiteIcon />
            <Typography variant='h6'>Website Data</Typography>
            {profile.websiteData && (
              <Chip label='Available' size='small' color='success' />
            )}
          </Box>
          <IconButton size='small'>
            {expandedSections.website ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </DataSectionHeader>

        <Collapse in={expandedSections.website}>
          <DataContent>
            {profile.websiteData ? (
              <JSONBlock>
                {JSON.stringify(profile.websiteData, null, 2)}
              </JSONBlock>
            ) : (
              <EmptyState small>
                <Typography color='textSecondary'>
                  No website data available
                </Typography>
              </EmptyState>
            )}
          </DataContent>
        </Collapse>
      </DataSection>

      {/* Instagram Data */}
      <DataSection>
        <DataSectionHeader onClick={() => toggleSection('instagram')}>
          <Box display='flex' alignItems='center' gap={1}>
            <InstagramIcon />
            <Typography variant='h6'>Instagram Data</Typography>
            {profile.instagramData && (
              <Chip label='Available' size='small' color='success' />
            )}
          </Box>
          <IconButton size='small'>
            {expandedSections.instagram ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )}
          </IconButton>
        </DataSectionHeader>

        <Collapse in={expandedSections.instagram}>
          <DataContent>
            {profile.instagramData ? (
              <JSONBlock>
                {JSON.stringify(profile.instagramData, null, 2)}
              </JSONBlock>
            ) : (
              <EmptyState small>
                <Typography color='textSecondary'>
                  No Instagram data available
                </Typography>
              </EmptyState>
            )}
          </DataContent>
        </Collapse>
      </DataSection>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  )
}
